import { ContentControlCanvasService } from "app/js/src/infrastructure/content-control/content-control-canvas.service";
import { IContentControlManager } from 'app/js/src/domain/content-control/manager';
import { IContentControlStudioTimeline } from "../content-control-studio-timeline/i-content-control-studio-timeline";
import {
  ContentControlledEditingDirective,
  ContentControlledEditingDirectiveViewModel,
} from 'app/js/src/domain/content-control/editing-directive';
import { ContentControlledKeyframe } from 'app/js/src/domain/content-control/keyframe';
import { ContentControlledEventActionEnum } from 'app/js/src/domain/content-control/enums';
import {
  CCBlurRect,
  CCMosaicRect,
  CCRect,
  ICanvasBaseObject,
} from 'app/js/src/domain/content-control/canvas';
import { IStudioViewModelContext } from "./i-studio-view-model-context";
import { StudioPlaybackController } from "./studio-playback-controller";
import { StudioSegmentController } from "./studio-segment-controller";

export class EffectAreaInteractionHandler {
  isEffectAreaDragging = false;
  isEffectAreaResizing = false;
  effectAreadResizeBox = "";
  effectAreaOffsetX = 0;
  effectAreaOffsetY = 0;
  keyframeChangeHolder: ContentControlledKeyframe = null;

  private _segments: StudioSegmentController;

  constructor(
    private readonly canvasService: ContentControlCanvasService,
    private readonly _manager: IContentControlManager,
    private readonly _timeline: IContentControlStudioTimeline,
    private readonly _drawingCanvas: HTMLElement,
    private readonly _context: IStudioViewModelContext,
    private readonly _playback: StudioPlaybackController,
  ) {}

  setSegmentController(segments: StudioSegmentController): void {
    this._segments = segments;
  }

  getCanvasObjectFromEvent(
    event: ContentControlledEditingDirective,
    forVideoEditing = false,
    keyframe?: ContentControlledKeyframe,
  ): ICanvasBaseObject {
    const manager = this._manager;
    const { x, y, width, height, isTween } =
      keyframe || event.getKeyframeByTime(this._context.currentVideoTimeFn(), forVideoEditing);

    if (
      event.id == manager.selectedSegment?.id &&
      (event.action == ContentControlledEventActionEnum.Empty ||
        manager.selectedSegment.isConfigurationComplete == false)
    ) {
      this.canvasService.resetDrawEffectArea(
        { x, y, width, height },
        keyframe || event.isKeyframeTween(this._context.currentVideoTimeFn()) ? "tween" : "",
        event.instructions?.scaleFactor,
      );
      this.canvasService.drawEffectAreaSVG = true;
    } else if (
      this._segments?.multiEventsSelectedIds.length > 0 &&
      this._segments.multiEventsSelectedIds.includes(event.id)
    ) {
      this.canvasService.addTempDrawEffectArea(
        event.id,
        { x, y, width, height },
        event.instructions?.scaleFactor,
      );
    }

    switch (event.action) {
      case ContentControlledEventActionEnum.Empty:
        break;
      case ContentControlledEventActionEnum.BlackScreen:
      case ContentControlledEventActionEnum.BlackOval:
      case ContentControlledEventActionEnum.BlackRect:
        return new CCRect(event.id, x, y, width, height, forVideoEditing, event.instructions?.scaleFactor);
      case ContentControlledEventActionEnum.PixelateScreen:
      case ContentControlledEventActionEnum.PixelateRect:
      case ContentControlledEventActionEnum.PixelateOval:
        return new CCMosaicRect(event.id, x, y, width, height, event.instructions.density, isTween, event.instructions?.scaleFactor);
      case ContentControlledEventActionEnum.BlurScreen:
      case ContentControlledEventActionEnum.BlurOval:
      case ContentControlledEventActionEnum.BlurRect:
        return new CCBlurRect(event.id, x, y, width, height, event.instructions.sampleDistance, isTween, event.instructions?.scaleFactor);
    }
    return null;
  }

  onMouseDown(event: any): void {
    if (
      this.canvasService.drawEffectAreaSVG == false ||
      !this._manager.selectedSegment ||
      (this._segments?.multiEventsSelectedIds.length ?? 0) > 0
    ) {
      return;
    }
    const rect = this._drawingCanvas?.getBoundingClientRect();
    const clickX = (event.clientX - rect.left) * this.canvasService.canvasesXOffsetRatio;
    const clickY = (event.clientY - rect.top) * this.canvasService.canvasesYOffsetRatio;
    const isDrawEffectAreaElement = this.canvasService.drawEffectAreaClicked(clickX, clickY);
    if (isDrawEffectAreaElement) {
      this._playback.pause();
      this.effectAreaOffsetX = clickX - this.canvasService.drawEffectRect.x;
      this.effectAreaOffsetY = clickY - this.canvasService.drawEffectRect.y;
      if (isDrawEffectAreaElement === "frame") {
        this.isEffectAreaDragging = true;
      } else {
        this.effectAreadResizeBox = isDrawEffectAreaElement;
        this.isEffectAreaResizing = true;
      }
    }
  }

  onMouseMove(event: any): void {
    const manager = this._manager;
    const timeline = this._timeline;
    if (
      this.canvasService.drawEffectAreaSVG == false ||
      !manager.selectedSegment ||
      (this._segments?.multiEventsSelectedIds.length ?? 0) > 0
    ) {
      return;
    }
    const rect = this._drawingCanvas?.getBoundingClientRect();
    const mouseX = (event.clientX - rect.left) * this.canvasService.canvasesXOffsetRatio;
    const mouseY = (event.clientY - rect.top) * this.canvasService.canvasesYOffsetRatio;

    if (this.isEffectAreaDragging) {
      const keyframe = manager.selectedEvent.getKeyframeByTime(
        this._context.getVideoSource().currentPlayTimeAsClosestFrameMs(), true,
      );
      this.keyframeChangeHolder = keyframe;
      keyframe.x = Math.max(0, (mouseX - this.effectAreaOffsetX) / this.canvasService.drawingCanvasWidthUnit);
      keyframe.y = Math.max(0, (mouseY - this.effectAreaOffsetY) / this.canvasService.drawingCanvasHeightUnit);
      if (keyframe.x + keyframe.width > 100) keyframe.x -= keyframe.x + keyframe.width - 100;
      if (keyframe.y + keyframe.height > 100) keyframe.y -= keyframe.y + keyframe.height - 100;
      const { x, y, width, height } = keyframe;
      this.canvasService.resizeDrawEffectArea(x, y, width, height);
      const canvasObject = this.getCanvasObjectFromEvent(manager.selectedEvent, false, keyframe);
      this.applyCanvasUpdate(manager, canvasObject);
      timeline.rebuildSegmentsInternally(this._segments?.segments ?? []);
    } else if (this.isEffectAreaResizing) {
      const keyframe = manager.selectedEvent.getKeyframeByTime(
        this._context.getVideoSource().currentPlayTimeAsClosestFrameMs(), true,
      );
      this.keyframeChangeHolder = keyframe;
      let px = keyframe.x * this.canvasService.drawingCanvasWidthUnit;
      let py = keyframe.y * this.canvasService.drawingCanvasHeightUnit;
      let pw = keyframe.width * this.canvasService.drawingCanvasWidthUnit;
      let ph = keyframe.height * this.canvasService.drawingCanvasHeightUnit;
      switch (this.effectAreadResizeBox) {
        case "top-left":     pw += px - mouseX; ph += py - mouseY; px = mouseX; py = mouseY; break;
        case "top-middle":   ph += py - mouseY; py = mouseY; break;
        case "top-right":    pw = mouseX - px; ph += py - mouseY; py = mouseY; break;
        case "right-middle": pw = mouseX - px; break;
        case "right-bottom": pw = mouseX - px; ph = mouseY - py; break;
        case "bottom-middle":ph = mouseY - py; break;
        case "bottom-left":  pw += px - mouseX; ph = mouseY - py; px = mouseX; break;
        case "left-middle":  pw += px - mouseX; px = mouseX; break;
      }
      keyframe.x = px / this.canvasService.drawingCanvasWidthUnit;
      keyframe.y = py / this.canvasService.drawingCanvasHeightUnit;
      keyframe.width = pw / this.canvasService.drawingCanvasWidthUnit;
      keyframe.height = ph / this.canvasService.drawingCanvasHeightUnit;
      const { x, y, width, height } = keyframe;
      this.canvasService.resizeDrawEffectArea(x, y, width, height);
      const canvasObject = this.getCanvasObjectFromEvent(manager.selectedEvent, true, keyframe);
      this.applyCanvasUpdate(manager, canvasObject);
      timeline.rebuildSegmentsInternally(this._segments?.segments ?? []);
    } else {
      const isDrawEffectAreaElement = this.canvasService.drawEffectAreaClicked(mouseX, mouseY);
      this.canvasService.drawEffectRect.highlightArea = isDrawEffectAreaElement;
    }
  }

  onMouseUp(event: any): void {
    const manager = this._manager;
    const timeline = this._timeline;
    try {
      if (this.isEffectAreaDragging || this.isEffectAreaResizing) {
        if (this.keyframeChangeHolder) {
          manager.updateSelectedEventKeyframe(this.keyframeChangeHolder);
        }
        setTimeout(() => timeline.rebuildSegmentsInternally(this._segments?.segments ?? []), 0);
      }
    } finally {
      // The holder and drag flags must always release - if they leak, the
      // render loop keeps rendering the selected event from the stale holder
      // and the effect rectangle freezes in place.
      this.keyframeChangeHolder = null;
      this.isEffectAreaDragging = false;
      this.isEffectAreaResizing = false;
      this.effectAreadResizeBox = "";
    }
  }

  private applyCanvasUpdate(manager: IContentControlManager, canvasObject: ICanvasBaseObject): void {
    switch (manager.selectedEvent.action) {
      case ContentControlledEventActionEnum.BlackRect:
        this.canvasService.updateBlackRectangle(canvasObject as CCRect); break;
      case ContentControlledEventActionEnum.PixelateRect:
        this.canvasService.updateMosaicRectangle(canvasObject as CCMosaicRect); break;
      case ContentControlledEventActionEnum.BlurRect:
        this.canvasService.updateBlurRectangle(canvasObject as CCBlurRect); break;
      case ContentControlledEventActionEnum.PixelateOval:
        this.canvasService.updateMosaicCircle(canvasObject as CCMosaicRect); break;
      case ContentControlledEventActionEnum.BlurOval:
        this.canvasService.updateBlurCircle(canvasObject as CCBlurRect); break;
      case ContentControlledEventActionEnum.BlackOval:
        this.canvasService.updateBlackCircle(canvasObject as CCRect); break;
    }
  }
}
